import {
  Alert,
  Badge,
  Checkbox,
  Input,
  message,
  Modal,
  ModalProps,
  Pagination,
  Table,
  TableColumnsType,
  TableColumnType,
} from 'antd';
import CheckContainer from 'components/CheckContainer';
import StatusButton from 'components/StatusButton';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import useMixingTokoText from 'hooks/business/useMixingTokoText';
import useCrud from 'hooks/useCrud';
import { useEffect, useRef, useState, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslate } from '@tolgee/react';
import { isNoRegionScenario, scenarioTypeI18nLabelMap } from 'views/Aide/Scenario/config';
import { bindStore, policyList, UnsetPolicyStore } from '../service';
import { Container, Content, StoreSelector, Task, TaskSelector } from './MoveStoreModal';
import { LoadingOutlined } from '@ant-design/icons';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

export interface NewMoveStoreModalProps extends ModalProps {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion | 'ALL';
  round: number;
  storeList: UnsetPolicyStore[];
  onUpdate: () => void;
}

function NewMoveStoreModal(props: NewMoveStoreModalProps) {
  const { visible, scenarioType, platform, region, round, storeList, onUpdate, ...otherProps } =
    props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { config: storeNameConfig } = useStoreNameConfig();

  const {
    orderReminderTitle: { text: orderReminderTitle },
  } = useMixingTokoText();

  const { loading, item, fetch } = useCrud({
    read: policyList,
  });

  const [selectedStoreMap, setSelectedStoreMap] = useState<
    Partial<Record<number, UnsetPolicyStore>>
  >({});

  const [selectedPolicyMap, setSelectedPolicyMap] = useState<Partial<Record<string, Aide.Policy>>>(
    {},
  );

  const policys = item ? (
    item.policy.map((policy) => {
      const { id, name, enable, knowledgeId } = policy;

      if (knowledgeId) {
        return null;
      }

      return (
        <CheckContainer
          key={id}
          checked={Boolean(selectedPolicyMap[id])}
          onChange={(checked) => {
            setSelectedPolicyMap((pre) => {
              if (checked) {
                return {
                  ...pre,
                  [id]: policy,
                };
              }
              const { [id]: _, ...others } = pre;
              return others;
            });
          }}
        >
          <Task>
            <div title={name}>{name}</div>
          </Task>
          <Badge
            status={enable ? 'success' : 'default'}
            text={enable ? t('marketing.启用中') : t('marketing.未启用')}
          />
        </CheckContainer>
      );
    })
  ) : (
    <LoadingOutlined />
  );

  const selectedPolicyList = Object.values(selectedPolicyMap);

  const policySelector = (
    <TaskSelector>
      <div>
        <h3>{t('department.选择x', { x: t('policy') })}</h3>
        {policys}
      </div>
      <footer>
        {t('marketing.已选 x', { x: selectedPolicyList.map((_) => _?.name).join(' ') })}
      </footer>
    </TaskSelector>
  );

  const [keywords, setKeywords] = useState('');

  const trimedKeywords = keywords.trim();

  const [filterRegionList, setFilterRegionList] = useState<General.UpperCaseRegion[]>([]);

  const filteredList = useMemo(() => {
    const regionFilteredList =
      filterRegionList.length === 0
        ? storeList || []
        : (storeList || []).filter(({ region }) => filterRegionList.includes(region));

    if (!trimedKeywords) {
      return regionFilteredList;
    }

    return regionFilteredList.filter(({ storeName, alias, storeId }) => {
      return (
        storeName.toLowerCase().includes(trimedKeywords.toLowerCase()) ||
        alias.toLowerCase().includes(trimedKeywords.toLowerCase()) ||
        `${storeId}`.includes(trimedKeywords)
      );
    });
  }, [storeList, trimedKeywords, filterRegionList]);

  const regionList = useMemo(() => {
    return (storeList || []).reduce<General.UpperCaseRegion[]>((res, { region }) => {
      if (res.includes(region)) {
        return res;
      }

      res.push(region);

      return res;
    }, []);
  }, [storeList]);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const displayList = useMemo(() => {
    return filteredList.slice((current - 1) * pageSize, current * pageSize);
  }, [filteredList, current, pageSize]);

  const storeChecked = (store: UnsetPolicyStore) =>
    Boolean(selectedStoreMap[store.internalStoreId]);

  const currentPageChecked = displayList.every(storeChecked);
  const currentPageIndeterminate = !currentPageChecked && displayList.some(storeChecked);

  const columns: TableColumnsType<UnsetPolicyStore> = [
    ...(displayList.length > 0
      ? [
          {
            key: 'checked',
            title: (
              <Checkbox
                checked={currentPageChecked}
                indeterminate={currentPageIndeterminate}
                onChange={({ target: { checked } }) => {
                  setSelectedStoreMap((pre) => {
                    if (checked) {
                      return displayList.reduce(
                        (res, cur) => {
                          res[cur.internalStoreId] = cur;
                          return res;
                        },
                        { ...pre },
                      );
                    }

                    return displayList.reduce(
                      (res, cur) => {
                        delete res[cur.internalStoreId];
                        return res;
                      },
                      { ...pre },
                    );
                  });
                }}
              />
            ),
            width: 20,
            align: 'center',
            render: (_, store: UnsetPolicyStore) => {
              return (
                <Checkbox
                  checked={storeChecked(store)}
                  onChange={({ target: { checked } }) => {
                    setSelectedStoreMap((pre) => {
                      if (checked) {
                        return {
                          ...pre,
                          [store.internalStoreId]: store,
                        };
                      }
                      const { [store.internalStoreId]: _, ...others } = pre;
                      return others;
                    });
                  }}
                />
              );
            },
          } as TableColumnType<UnsetPolicyStore>,
        ]
      : []),
    ...(isNoRegionScenario(scenarioType)
      ? [
          {
            key: 'region',
            dataIndex: 'region',
            title: t('aide.站点'),
            width: 150,
            filters: regionList.map((region) => {
              return {
                value: region,
                text: getRegionLabel(region),
              };
            }),
            render: (region: UnsetPolicyStore['region']) => getRegionLabel(region),
          } as const,
        ]
      : []),
    {
      key: 'storeName',
      title: t('marketing.店铺名称'),
      width: isNoRegionScenario(scenarioType) ? 250 : 400,
      render: (_, record: UnsetPolicyStore) => {
        const { region, storeName, alias } = record;

        const displayName = isStoreAliasFirst(storeNameConfig, platform, region)
          ? alias || storeName
          : storeName;

        return <Highlighter autoEscape searchWords={[keywords]} textToHighlight={displayName} />;
      },
    },
  ];

  const table = (
    <Table
      rowKey="storeId"
      loading={loading}
      columns={columns}
      dataSource={displayList}
      pagination={false}
      onChange={(_, { region }) => setFilterRegionList((region as any) || [])}
    />
  );

  const selectedStoreLength = Object.keys(selectedStoreMap).length;

  const footer = storeList.length > 0 && (
    <footer>
      <div>
        <Checkbox
          checked={storeList.length === selectedStoreLength}
          indeterminate={storeList.length > selectedStoreLength && selectedStoreLength > 0}
          onChange={(e) =>
            setSelectedStoreMap(
              e.target.checked
                ? storeList.reduce<Partial<Record<string, UnsetPolicyStore>>>((res, cur) => {
                    res[cur.internalStoreId] = cur;
                    return res;
                  }, {})
                : {},
            )
          }
        >
          {t('marketing.全选')}
        </Checkbox>
        <div>{t('marketing.已选 x', { x: selectedStoreLength })}</div>
      </div>
      <Pagination
        size="small"
        current={current}
        pageSize={pageSize}
        total={filteredList.length}
        onChange={(current, pageSize) => {
          setCurrent(current);
          setPageSize(pageSize);
        }}
        showSizeChanger={false}
      />
    </footer>
  );

  const storeSelector = (
    <StoreSelector>
      <div>
        <h3>
          {`${t('marketing.店铺')}(${(storeList || []).length})`}
          <Input.Search
            allowClear
            placeholder={t('aide.请输入店铺名称/店铺别名/官方ID')}
            onSearch={(keywords) => {
              setKeywords(keywords);
              setCurrent(1);
            }}
          />
        </h3>
        {table}
      </div>
      {footer}
    </StoreSelector>
  );

  const alert = (
    <Alert
      banner
      type="warning"
      message={t('unset_store_of_policy_tips', {
        x:
          scenarioType === 'ORDER_REMINDER'
            ? orderReminderTitle
            : scenarioType === 'PRAISE_REMINDER' && isTiKTokUpperCase(platform)
            ? t('邀请评价')
            : t(scenarioTypeI18nLabelMap[scenarioType]),
      })}
    />
  );

  const content = (
    <Content>
      {storeSelector}
      {policySelector}
    </Content>
  );

  const refsCurrent = {
    t,
    fetch,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (!visible) {
      return;
    }

    refs.current
      .fetch({
        scenarioType,
        platform,
        region,
        round,
        policyValidType: 'ALL',
        pageNum: 1,
        pageSize: 99999,
      })
      .catch(() => {
        message.error(refs.current.t('phrase.获取数据失败，请稍后再试'));
      });
  }, [visible, scenarioType, platform, region, round]);

  const reset = () => {
    setSelectedStoreMap({});
    setSelectedPolicyMap({});
  };

  const saveButton = (
    <StatusButton
      disabled={selectedStoreLength === 0 || selectedPolicyList.length === 0}
      type="primary"
      onClick={() =>
        bindStore({
          policyIds: Object.values(selectedPolicyMap).reduce<number[]>((res, cur) => {
            if (cur) {
              res.push(cur.id);
            }
            return res;
          }, []),
          stores: Object.values(selectedStoreMap).reduce<UnsetPolicyStore[]>((res, cur) => {
            if (cur) {
              res.push(cur);
            }
            return res;
          }, []),
        })
          .then(() => {
            reset();
            onUpdate();
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          })
      }
    >
      {t('aide.保存并生效')}
    </StatusButton>
  );

  return (
    <Modal
      title={t('unset_store_of_policy')}
      visible={visible}
      width={800}
      maskClosable={false}
      footer={saveButton}
      {...otherProps}
    >
      <Container>
        {alert}
        {content}
      </Container>
    </Modal>
  );
}

export default NewMoveStoreModal;
